import { APP_INITIALIZER, ApplicationConfig, inject } from '@angular/core';
import {
  NavigationError,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withNavigationErrorHandler,
  withRouterConfig,
} from '@angular/router';

import { AppRoutes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { angularApiHttpClient } from '@api/client/angularApiHttpClient';
import { AppApi } from '@api/AppApi';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { authJwtInterceptor } from '@core/interceptors/auth-jwt.interceptor';
import { httpErrorInterceptor } from '@core/interceptors/http-error.interceptor';
import { RouterErrorHandlerService } from '@core/services/router-error-handler/router-error-handler.service';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { PublicConfigService } from '@core/services/public-config/public-config.service';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import {
  MAT_TOOLTIP_HIDE_DELAY,
  MAT_TOOLTIP_SHOW_DELAY,
} from '@shared/constants/constants';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      AppRoutes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withComponentInputBinding(),
      withNavigationErrorHandler((e: NavigationError) =>
        inject(RouterErrorHandlerService).trackError(e),
      ),
    ),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptors([authJwtInterceptor, httpErrorInterceptor]),
    ),
    {
      provide: AppApi,
      useFactory: (http: HttpClient): AppApi => {
        return new AppApi(angularApiHttpClient(http));
      },
      deps: [HttpClient],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (navigationService: NavigationService) => () =>
        navigationService,
      deps: [NavigationService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (publicConfigService: PublicConfigService) => () =>
        publicConfigService.getConfig(),
      deps: [PublicConfigService],
      multi: true,
    },
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      isolate: false,
      defaultLanguage: 'en',
    }),
    { provide: MAT_DATE_LOCALE, useValue: navigator.language },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: MAT_TOOLTIP_SHOW_DELAY,
        hideDelay: MAT_TOOLTIP_HIDE_DELAY,
      },
    },
  ],
};

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
